import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Badge,
  IconButton,
  List,
  Typography,
  CircularProgress,
  Box,
  Divider,
  Button,
} from '@mui/material';
import { Z_INDEX } from '../../constants/zIndex';
import BulkNotificationDetails from './BulkNotificationDetails';
import MultiNotificationDetails from './MultiNotificationDetails';
import NotificationRow from './NotificationRow';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import useAuth from 'src/hooks/useAuth';
import { useNotifications } from 'src/contexts/NotificationContext';
import { getNotificationNavigationUrl } from 'src/utils/notificationNavigation';
import { getNotificationSourceValue } from 'src/utils/notificationFields';
import MenuPopover from 'src/components/MenuPopover';
import { useEventTracking } from 'src/hooks/useEventTracking';

import {
  Notification,
  NotificationBellProps,
  isMultiNotification,
  isBulkNotification,
} from 'src/types/Notifications';

// CSS animation for new notification emphasis
const newNotificationStyles = `
  @keyframes newNotificationPulse {
    0% {
      background-color: rgba(25, 118, 210, 0.1);
      transform: scale(1);
    }
    50% {
      background-color: rgba(25, 118, 210, 0.2);
      transform: scale(1.02);
    }
    100% {
      background-color: inherit;
      transform: scale(1);
    }
  }
`;

export const formatActionType = (actionType: string): string => {
  const actionMap: Record<string, string> = {
    bulk_file_owner_assignment: 'File Owner Assignment',
    bulk_status_change: 'Status Change',
    bulk_property_transfer: 'Property Transfer',
    // Add more action types as needed
  };
  return (
    actionMap[actionType] ||
    actionType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

// In-app notification settings (snake_case to match backend)
const IN_APP_NOTIFICATION_SETTINGS = [
  // File Owner notifications
  'receive_new_email_notifications',
  'receive_new_documents_notifications',
  'receive_new_file_owner_notifications',
  'receive_new_sms_notifications',
  'receive_new_fax_notifications',
  'receive_new_status_note_notifications',
  // Watched notifications
  'watched_receive_new_email_notifications',
  'watched_receive_new_documents_notifications',
  'watched_receive_new_file_owner_notifications',
  'watched_receive_new_sms_notifications',
  'watched_receive_new_fax_notifications',
  'watched_receive_new_status_note_notifications',
  // Property notifications
  'receive_property_email_notifications',
  'receive_property_fax_notifications',
  'receive_property_sms_notifications',
] as const;

const NotificationBell: React.FC<NotificationBellProps> = ({
  iconSize = 24,
  isOpen,
  onToggle,
  onClose,
  notificationSourceFilter,
}) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const iconButtonRef = useRef<HTMLButtonElement>(null);
  const [selectedBulkNotification, setSelectedBulkNotification] =
    useState<Notification | null>(null);
  const [selectedMultiNotification, setSelectedMultiNotification] =
    useState<Notification | null>(null);
  const {
    trackDropdownAction,
    trackNotificationClick,
    trackNotificationMarkRead,
    trackNotificationMarkUnread,
    trackNotificationBulkDetailsOpen,
  } = useEventTracking();
  const [newNotificationIds, setNewNotificationIds] = useState<Set<number>>(
    new Set()
  );
  const seenNotificationIdsRef = useRef<Set<number>>(new Set());

  const {
    unreadCount,
    notifications,
    loading,
    error,
    markAsRead,
    bulkMarkAsRead,
    markAsUnread,
    bulkMarkAsUnread,
    markAllAsRead,
  } = useNotifications();

  const isResident = Boolean(
    user?.isResident ||
      user?.role === 'resident' ||
      user?.groups?.some((group) => group?.name?.toLowerCase() === 'resident')
  );
  const hasAnyNotificationPermission = isResident
    ? true
    : user?.canUseRealTimeNotificationsFileOwner ||
      user?.canUseRealTimeNotificationsWatched ||
      user?.canUseRealTimeNotificationsProperty;

  // Memoize visibleNotifications: filter by source, then group recert_tracking by property
  const visibleNotifications = useMemo(() => {
    const filtered = notificationSourceFilter
      ? notifications.filter(
          (notification) =>
            getNotificationSourceValue(notification) ===
            notificationSourceFilter
        )
      : notifications;
    return filtered;
  }, [notifications, notificationSourceFilter]);

  // Derive unread count from visible notification list for badge and empty state
  const visibleUnreadCount = useMemo(() => {
    return visibleNotifications.filter((notification) => !notification.is_read)
      .length;
  }, [visibleNotifications]);

  useEffect(() => {
    // Check for new notifications and animate them
    const prevSeen = seenNotificationIdsRef.current;
    const trulyNewIds = new Set<number>();
    visibleNotifications.forEach((notification) => {
      if (!prevSeen.has(notification.id)) {
        trulyNewIds.add(notification.id);
      }
    });
    // Update the seen set for next check
    seenNotificationIdsRef.current = new Set(
      visibleNotifications.map((n) => n.id)
    );
    if (trulyNewIds.size > 0) {
      setNewNotificationIds(trulyNewIds);
      setTimeout(() => {
        setNewNotificationIds(new Set());
      }, 3000);
    }
  }, [visibleNotifications]);

  const handleBellClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (!hasAnyNotificationPermission) {
      return;
    }

    // Use external state management if provided, otherwise use internal state
    if (onToggle) {
      onToggle();
    } else {
      if (anchorEl && anchorEl.contains(event.target as Node)) {
        setAnchorEl(null);
        return;
      }
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = (): void => {
    if (onClose) {
      onClose();
    } else {
      setAnchorEl(null);
    }
  };

  const handleNotificationClick = async (
    notif: Notification
  ): Promise<void> => {
    // Track notification click
    trackNotificationClick(notif.notification_type, String(notif.id), {
      notification_id: notif.id,
      notification_type: notif.notification_type,
      notification_type_category: notif.notification_type_category,
      is_read: notif.is_read,
      is_watched: notif.is_watched,
      is_single_notification: notif.is_single_notification,
      is_bulk_notification: notif.is_bulk_notification,
      is_property_notification: notif.is_property_notification,
      is_system_notification: notif.is_system_notification,
      page_context: 'notification',
      detail_type: 'header',
      debug_info: {
        component_name: 'NotificationBell',
        function_name: 'handleNotificationClick',
      },
    });

    const groupedIds = notif.related_objects?._groupedIds;
    if (groupedIds?.length) {
      // Only call bulk mark-as-read when group has unread items (avoids over-decrement of unread count)
      if (!notif.is_read) {
        try {
          await bulkMarkAsRead(groupedIds);
        } catch {
          enqueueSnackbar('Failed to mark notifications as read.', {
            variant: 'error',
          });
        }
      }
    } else if (!notif.is_read) {
      try {
        await markAsRead(notif.id);
      } catch {
        enqueueSnackbar('Failed to mark notification as read.', {
          variant: 'error',
        });
      }
    }
    handleClose();

    if (isResident) {
      navigate('/user/notifications');
      return;
    }

    // Handle navigation for different notification types
    if (isBulkNotification(notif)) {
      // Track bulk details open
      trackNotificationBulkDetailsOpen(notif.id, {
        page_context: 'notification',
        detail_type: 'header',
        debug_info: {
          component_name: 'NotificationBell',
          function_name: 'handleNotificationClick',
        },
      });
      // For bulk notifications, always open the details modal (ignore URL)
      setSelectedBulkNotification(notif);
    } else if (isMultiNotification(notif)) {
      // Track multi details open (reuse bulk tracking for now)
      trackNotificationBulkDetailsOpen(notif.id, {
        page_context: 'notification',
        detail_type: 'header',
        debug_info: {
          component_name: 'NotificationBell',
          function_name: 'handleNotificationClick',
        },
      });
      // For multi-notifications, open the multi details modal
      setSelectedMultiNotification(notif);
    } else {
      // Use centralized navigation utility for all other notification types
      const navigationUrl = getNotificationNavigationUrl(notif);
      if (navigationUrl) {
        if (notif.notification_type === 'new_documents') {
          window.open(navigationUrl, '_blank');
        } else {
          navigate(navigationUrl);
        }
      }
    }
  };

  const handleMarkAllRead = async (): Promise<void> => {
    // Track mark all as read action
    trackDropdownAction('notifications', 'mark_all_read', {});

    try {
      await markAllAsRead();
    } catch {
      enqueueSnackbar('Failed to mark all as read.', { variant: 'error' });
    }
  };

  const handleMarkAsRead = async (
    notifId: number,
    e: React.MouseEvent
  ): Promise<void> => {
    e.stopPropagation();

    // Track individual mark as read action
    const notification = notifications.find((n) => n.id === notifId);
    if (notification) {
      trackNotificationMarkRead(notifId, {
        notification_type: notification.notification_type,
        notification_type_category: notification.notification_type_category,
        is_read: notification.is_read,
        is_watched: notification.is_watched,
        is_single_notification: notification.is_single_notification,
        is_bulk_notification: notification.is_bulk_notification,
        is_property_notification: notification.is_property_notification,
        is_system_notification: notification.is_system_notification,
        page_context: 'notification',
        detail_type: 'header',
        debug_info: {
          component_name: 'NotificationBell',
          function_name: 'handleMarkAsRead',
        },
      });
    }

    try {
      const groupedIds = notification?.related_objects?._groupedIds;
      if (groupedIds?.length) {
        await bulkMarkAsRead(groupedIds);
      } else {
        await markAsRead(notifId);
      }
    } catch {
      enqueueSnackbar('Failed to mark notification as read.', {
        variant: 'error',
      });
    }
  };

  const handleMarkAsUnread = async (
    notifId: number,
    e: React.MouseEvent
  ): Promise<void> => {
    e.stopPropagation();

    // Track individual mark as unread action
    const notification = notifications.find((n) => n.id === notifId);
    if (notification) {
      trackNotificationMarkUnread(notifId, {
        notification_type: notification.notification_type,
        notification_type_category: notification.notification_type_category,
        is_read: notification.is_read,
        is_watched: notification.is_watched,
        is_single_notification: notification.is_single_notification,
        is_bulk_notification: notification.is_bulk_notification,
        is_property_notification: notification.is_property_notification,
        is_system_notification: notification.is_system_notification,
        page_context: 'notification',
        detail_type: 'header',
        debug_info: {
          component_name: 'NotificationBell',
          function_name: 'handleMarkAsUnread',
        },
      });
    }

    try {
      const groupedIds = notification?.related_objects?._groupedIds;
      if (groupedIds?.length) {
        await bulkMarkAsUnread(groupedIds);
      } else {
        await markAsUnread(notifId);
      }
    } catch {
      enqueueSnackbar('Failed to mark notification as unread.', {
        variant: 'error',
      });
    }
  };

  // Determine if popover should be open
  const isPopoverOpen = onToggle ? isOpen : Boolean(anchorEl);

  // Disabled state if all in-app notification types are off
  const renterInAppPreference = user?.receive_renter_notifications;
  const inAppDisabled = isResident
    ? renterInAppPreference === false
    : IN_APP_NOTIFICATION_SETTINGS.every((setting) => !user?.[setting]);

  return (
    <>
      <style>{newNotificationStyles}</style>
      <span>
        <IconButton
          ref={iconButtonRef}
          color="inherit"
          onClick={handleBellClick}
          disabled={!hasAnyNotificationPermission || inAppDisabled}
          size="large"
          sx={{
            outline: 'none',
            boxShadow: 'none',
            backgroundColor: 'background.paper',
            borderRadius: '50%',
            p: 0,
            m: 0,
            width: iconSize,
            height: iconSize,
            minWidth: iconSize,
            minHeight: iconSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:focus': { outline: 'none', boxShadow: 'none' },
            '&:active': { outline: 'none', boxShadow: 'none' },
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <Badge
            badgeContent={visibleUnreadCount > 0 ? visibleUnreadCount : null}
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': { boxShadow: 'none', outline: 'none' },
            }}
          >
            {visibleUnreadCount > 0 ? (
              <NotificationsActiveIcon
                color="primary"
                sx={{ fontSize: iconSize }}
              />
            ) : (
              <NotificationsIcon
                color={inAppDisabled ? 'disabled' : 'action'}
                sx={{ fontSize: iconSize }}
              />
            )}
          </Badge>
        </IconButton>
      </span>
      <MenuPopover
        open={isPopoverOpen}
        onClose={handleClose}
        anchorEl={iconButtonRef.current}
        sx={{ width: { xs: '95vw', sm: 480, md: 660 }, maxWidth: '100vw' }}
        container={document.body}
        slotProps={{
          paper: {
            sx: {
              zIndex: Z_INDEX.HEADER_DROPDOWN,
            },
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="subtitle1">Unread Notifications</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              Click anywhere in a row to open the certification or certification
              list
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={handleMarkAllRead}
            disabled={visibleUnreadCount === 0}
          >
            Mark all as read
          </Button>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 2 }}>
            {error}
          </Typography>
        ) : visibleUnreadCount === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationsIcon
              sx={{
                fontSize: 48,
                color: 'text.secondary',
                mb: 1,
                opacity: 0.6,
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              You&apos;re all caught up!
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                opacity: 0.8,
              }}
            >
              No unread notifications at the moment.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {(() => {
              // Only show unread notifications
              const unreadNotifications = visibleNotifications
                .filter((notif) => !notif.is_read)
                .sort(
                  (a, b) =>
                    new Date(b.created).getTime() -
                    new Date(a.created).getTime()
                )
                .slice(0, 5); // Limit to 5 unread notifications

              return unreadNotifications.map((notif) => (
                <NotificationRow
                  key={notif.id}
                  notification={notif}
                  onNotificationClick={handleNotificationClick}
                  onMarkAsRead={handleMarkAsRead}
                  onMarkAsUnread={handleMarkAsUnread}
                  showTypeIcon={true}
                  showTimestamps={true}
                  isDropdown={true}
                  newNotificationIds={newNotificationIds}
                />
              ));
            })()}
          </List>
        )}
        {/* Footer: See All Notifications link */}
        <Divider sx={{ mt: 1, mb: 0.5 }} />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button
            size="small"
            color="primary"
            onClick={() => {
              // Track "See All Notifications" click
              trackDropdownAction('notifications', 'see_all_notifications', {
                action: 'navigate_to_notifications_page',
              });
              handleClose();
              if (isResident) {
                navigate('/user/notifications');
              } else {
                navigate('/workbench/notifications');
              }
            }}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            See All Notifications
          </Button>
        </Box>
      </MenuPopover>

      {/* Bulk Notification Details Modal */}
      <BulkNotificationDetails
        notification={selectedBulkNotification}
        open={!!selectedBulkNotification}
        onClose={() => setSelectedBulkNotification(null)}
      />

      {/* Multi Notification Details Modal */}
      <MultiNotificationDetails
        notification={selectedMultiNotification}
        open={!!selectedMultiNotification}
        onClose={() => setSelectedMultiNotification(null)}
      />
    </>
  );
};

export default NotificationBell;
